import {Component, OnInit, ViewChild, AfterViewInit, DestroyRef} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {DataService} from '../../../Services/data.service';
import { StorageService } from '../../../Services/storage.service';
import {ActivatedRoute, ParamMap, Router} from '@angular/router';
import { FilterPipe } from 'ngx-filter-pipe';
import {DateFilterService} from '../../../Services/dateFilter.service';
import { Title } from '@angular/platform-browser';
import { ConfirmDialogService } from '../../../Services/confirm-dialog.service';
import { BsToastService } from '../../../Services/bs-toast-service';
import {NavigationService} from '../../../Services/navigation.service';
import {forkJoin} from 'rxjs';
import {EmailModel} from '../../../DataModels/email-model';
import {TypeaheadBase} from '../../common/typeahead/typeahead-base';

@Component({
  selector: 'app-entry-form',
  templateUrl: './entry-form.component.html',
  styleUrls: ['./entry-form.component.css'],
  standalone: false
})
export class EntryFormComponent extends TypeaheadBase implements OnInit, AfterViewInit {
  vm: any = {
    recurring: false
  };

  // Accounts readiness and fast lookup map
  private accountsReady = false;
  private accountById: Record<string, any> = {};
  private pendingRebindAccounts = false;

  public records: any[] = [];
  public buttonsDisabled = false;
  public lockedBatch = 'false';
  public thisBatchId = '';
  public deleteMode = false;
  public urlParams: any = [];
  showImportButtons = false;
  value = '';
  // routedFrom is no longer used; NavigationService handles previous URL logic
  saveCloseUsed = false;

  @ViewChild('csvReader', { static: false }) csvReader: any;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private localStorage: StorageService,
    private tableDataService: DataService,
    private notification: BsToastService,
    private dateFilter: DateFilterService,
    private titleService: Title,
    private confirmDialogService: ConfirmDialogService,
    private nav: NavigationService,
    private destroyRef: DestroyRef,
  ) {
    super();
    this.vm.isLoaded = false;
    this.vm.startAccount = this.vm.entryTotal = 0;
    this.vm.newAccount = {};
    this.vm.Empty = 0;
    this.vm.accountFilter = { search_string: '' };
    this.vm.batches = 0;
    this.vm.thisBatch = null;
    this.vm.rfTypeLoaded = false;

    this.vm.item = {};
    this.vm.item.header = {periodicity: 'monthly'};
    this.vm.item.batch = '';
    this.vm.recurring = false;
    this.vm.csvResults = [];
    this.vm.account = [];
    this.vm.accountSubtotal = [];
    this.vm.company = [];

    this.vm.title = $localize`:Journal Entry @@journalEntry:Journal Entry`;

    this.urlParams = this.route.snapshot.queryParams;
    this.vm.entry_id = this.urlParams.entryId;
    this.vm.batchId = this.urlParams.batchId;
    this.vm.recurring = this.urlParams.recurring;
    this.thisBatchId = this.urlParams.thisBatchId;

    // Previous URL handling centralized in NavigationService; no local tracking needed.
  }

  buttonConfig: any = {
    save: true,
    close: true,
    reset: true,
    delete: true,
    saveNew: true,
    saveClose: true,
    clone: true,
    email: true,
    print: true,
    delivered: false,
    locked: false,
  };
  showBatches = false;
  submitted = false;
  pageLoadedFromPost = false;
  isExistingEntry = false;
  editNumber = false;

  // Baseline snapshot for unsaved-changes guard
  originalSnapshot: any = null;
  baselineReady = false;
  // One-shot flag to suppress guard during delete navigation
  private suppressGuardOnce = false;

  // Email modal binding object
  emailObject: EmailModel = new EmailModel();

  accountTypeaheadSearch = (text$: any) => this.typeaheadSearch(text$, () => this.getAccountNameList());

  // Magic string constants and query params
  private readonly PAGE_ALL = '?pageNumber=0&pageSize=999999';
  private readonly STATE_ACTIVE = 'state=active';
  private readonly API_REPORTING_FIELD = 'reporting_field';
  private readonly API_REPORTING_FIELD_TYPE = 'reporting_field_type';
  private readonly RF_CODES = ['rf1', 'rf2', 'rf3', 'rf4', 'rf5'];

  // Formatted total string for display in Balance row
  vmTotalFormatted: string = '0.00';

  ngOnInit() {
    this.baselineReady = false;
    this.vm.title = this.buildTabTitle(this.normalizeRecurringFlag(this.vm.recurring));
    this.titleService.setTitle(this.vm.title);

    // Parallel lookup data fetch for faster load and single RF fetch
    this.fetchLookupData();

    this.vm.Preferences = JSON.parse(this.localStorage.StorageGet('Preferences', ''));

    // If navigated here with a cloned item in navigation state, preload it
    try {
      const st: any = (window as any)?.history?.state || {};
      if (st && st.clonedItem) {
        this.vm.entry_id = 'new';
        this.vm.item = JSON.parse(JSON.stringify(st.clonedItem));
        this.vm.isLoaded = true;
        // Rebuild fast account index and bind helpers if needed
        try {
          this.rebuildAccountIndex();
        } catch {
        }
        try {
          if (this.pendingRebindAccounts) {
            Promise.resolve().then(() => this.rebindAccountInputs());
          }
        } catch {
        }
        // Recalculate vm.total from details to ensure balance state matches cloned data
        try {
          const details: any[] = Array.isArray(this.vm?.item?.detail) ? this.vm.item.detail : [];
          let total = 0;
          for (const d of details) {
            const val = (d && (d.amount !== undefined ? Number(d.amount) : Number(d.debit_credit))) || 0;
            total += Number.isFinite(val) ? val : 0;
          }
          this.vm.total = Math.round(total * 100) / 100;
          this.updateTotalFormatted();
        } catch {
          this.vm.total = 0;
          this.updateTotalFormatted();
        }
        try {
          this.setBaseline();
        } catch {
        }
        return; // skip normal load; we have a cloned model ready
      }
    } catch {
    }

    // Routing and document load
    this.getEntryData();
  }

  ngAfterViewInit() {
    // this.vm.isLoaded = true;
  }

  onPrint() {
    try {
      const header = this.vm?.item?.header || {};
      if (!header?.id) {
        this.notification.showWarningToast('Please save the entry before printing');
        return;
      }
      const html = this.buildEntryPrintHtml();
      this.printHtmlInIframe(html);
    } catch (e) {
      console.error('Print failed', e);
      this.notification.showErrorToast('Unable to print this entry');
    }
  }

  private printHtmlInIframe(html: string): void {
    // Create a hidden iframe and write the HTML into it, then trigger print
    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    iframe.setAttribute('aria-hidden', 'true');

    document.body.appendChild(iframe);

    const doc = iframe.contentDocument || iframe.contentWindow?.document;
    if (!doc) {
      this.notification.showErrorToast('Unable to access print frame');
      document.body.removeChild(iframe);
      return;
    }

    doc.open();
    doc.write(html);
    doc.close();

    // Give the browser a moment to apply styles, then print
    const printAndCleanup = () => {
      try {
        iframe.contentWindow?.focus();
        iframe.contentWindow?.print();
      } finally {
        // Cleanup after a short delay to allow print dialog to initialize
        setTimeout(() => {
          if (iframe && iframe.parentNode) {
            iframe.parentNode.removeChild(iframe);
          }
        }, 1000);
      }
    };

    // If the document has not finished loading, wait for it
    if (doc.readyState === 'complete') {
      setTimeout(printAndCleanup, 250);
    } else {
      iframe.onload = () => setTimeout(printAndCleanup, 250);
    }
  }

  onEmail() {
    try {
      const header = this.vm?.item?.header || {};
      if (!header?.id) {
        this.notification.showWarningToast('Please save the entry before emailing');
        return;
      }
      const html = this.buildEntryPrintHtml(true);
      const companyName = this.getCompanyNameById(header.company_id);
      const emailNumber = header.audit_sequence || header.number || header.id;
      const isRecurring = (() => {
        const v: any = header?.recurring;
        if (typeof v === 'boolean') {
          return v;
        }
        if (typeof v === 'number') {
          return v === 1;
        }
        if (typeof v === 'string') {
          const s = v.trim().toLowerCase();
          return s === 'true' || s === '1' || s === 'y' || s === 'yes';
        }
        return false;
      })();
      const entryLabel = isRecurring ? 'Recurring Journal Entry' : 'Journal Entry';

      this.emailObject = new EmailModel();
      this.emailObject.Subject = `${companyName ? companyName + ' - ' : ''}${entryLabel} ${emailNumber}`;
      this.emailObject.header_id = header.id;
      this.emailObject.batch_type_id = String(this.vm?.item?.batch?.type_id || '1');
      this.emailObject.FileName = `JournalEntry-${emailNumber}`;
      this.emailObject.convert_attachment_to = 'pdf';
      this.emailObject.print_orientation = 'landscape';
      this.emailObject.pdfHtml = html;
      this.emailObject.body = `Please see the attached ${entryLabel} ${emailNumber}.<br><br><br>Best regards,<br><br>AcctXErp<br><br>`;
      this.emailObject.includeAttachment = true;

      // Modal will pick up [emailObject] binding and handle send
    } catch (e) {
      console.error('Prepare email failed', e);
      this.notification.showErrorToast('Unable to prepare email for this entry');
    }
  }

  private getCompanyNameById(id: any): string {
    const list = Array.isArray(this.vm?.company) ? this.vm.company : [];
    const found = list.find((c: any) => String(c?.id) === String(id));
    return found?.name || '';
  }

  private buildEntryPrintHtml(forEmail: boolean = false): string {
    const item = this.vm?.item || {};
    const header = item?.header || {};
    const details: any[] = Array.isArray(item?.detail) ? item.detail : [];
    const companyName = this.getCompanyNameById(header.company_id);
    const date = header.header_date ? new Date(header.header_date) : new Date();
    const dateStr = date.toLocaleDateString();
    const journalNumber = header.audit_sequence || header.number || header.id || '';

    const getAccountDisplay = (account_id: any) => {
      const id = String(account_id ?? '').trim();
      if (!id) {
        return '';
      }
      // Prefer O(1) map if available
      const accFast = this.accountById ? this.accountById[id] : null as any;
      if (accFast) {
        return `${accFast.number} ${accFast.name}`;
      }
      const acc = (Array.isArray(this.vm?.account) ? this.vm.account : []).find((a: any) => String(a?.id) === id);
      return acc ? `${acc.number} ${acc.name}` : id;
    };

    const getLocationName = (id: any) => {
      const list = Array.isArray(this.vm?.location) ? this.vm.location : [];
      const found = list.find((l: any) => String(l?.id) === String(id));
      return found?.name || '';
    };

    const getRfDisplayLine = (obj: any) => {
      const rfById = this.vm?.rfById || {};
      const rfKeys = [
        {key: 'rf_id_1', label: 'RF1'},
        {key: 'rf_id_2', label: 'RF2'},
        {key: 'rf_id_3', label: 'RF3'},
        {key: 'rf_id_4', label: 'RF4'},
        {key: 'rf_id_5', label: 'RF5'},
      ];
      const parts: string[] = rfKeys.map(({key, label}) => {
        let val: any = obj?.[key];
        // if object provided
        if (val && typeof val === 'object') {
          val = val.id ?? val.value_id ?? val.value;
        }
        if (!val) {
          return '';
        }
        const rf = rfById[String(val)] || null;
        const name = rf?.name || rf?.code || String(val);
        return `${label}: ${name}`;
      }).filter(Boolean) as string[];
      return parts.join(' | ');
    };

    const rowsHtml = details.map((d: any, i: number) => {
      const dc = Number(d?.dc || (Number(d?.debit_credit) >= 0 ? 1 : -1));
      const amountAbs = Math.abs(Number(d?.amount ?? d?.debit_credit ?? 0));
      const debit = dc > 0 ? amountAbs.toFixed(2) : '';
      const credit = dc < 0 ? amountAbs.toFixed(2) : '';
      const dLocation = getLocationName(d?.location_id);
      const dRfLine = getRfDisplayLine(d);
      return `
        <tr>
          <td>${i + 1}</td>
          <td>${getAccountDisplay(d?.account_id)}</td>
          <td>${d?.description ?? ''}</td>
          <td>${dLocation}</td>
          <td>${dRfLine}</td>
          <td style="text-align:right">${debit}</td>
          <td style="text-align:right">${credit}</td>
        </tr>`;
    }).join('');

    const total = Number(item?.header?.detail_total ?? 0);

    const styles = `
      <style>
        @page { size: landscape; margin: 12mm; }
        @media print { .no-print { display: none !important; } }
        body { font-family: Arial, Helvetica, sans-serif; margin: 24px; }
        h1 { font-size: 18px; margin: 0 0 8px 0; }
        .muted { color: #666; }
        table { width: 100%; border-collapse: collapse; margin-top: 12px; }
        th, td { border: 1px solid #ccc; padding: 6px 8px; font-size: 12px; }
        th { background: #f6f6f6; text-align: left; }
        .totals { text-align: right; font-weight: bold; }
      </style>
    `;

    // Resolve Location name for header (safe on missing data)
    const locationName = (() => {
      const list = Array.isArray(this.vm?.location) ? this.vm.location : [];
      const found = list.find((l: any) => String(l?.id) === String(header?.location_id));
      return found?.name || '';
    })();

    // Resolve RF header values (RF1..RF5) with robust id mapping
    const rfById = this.vm?.rfById || {};
    const rfHeaderKeys = [
      {key: 'rf_id_1', label: 'RF1'},
      {key: 'rf_id_2', label: 'RF2'},
      {key: 'rf_id_3', label: 'RF3'},
      {key: 'rf_id_4', label: 'RF4'},
      {key: 'rf_id_5', label: 'RF5'},
    ];
    const rfParts: string[] = rfHeaderKeys
      .map(({key, label}) => {
        let val: any = (header as any)?.[key];
        if (val && typeof val === 'object') {
          val = val.id ?? val.value_id ?? val.value;
        }
        if (!val) {
          return '';
        }
        const rf = rfById[String(val)] || null;
        const name = rf?.name || rf?.code || String(val);
        return `${label}: ${name}`;
      })
      .filter(Boolean) as string[];
    const rfLineHtml = rfParts.length ? `<div class="muted">${rfParts.join(' | ')}</div>` : '';

    // Determine Recurring flag from header value (supports multiple representations)
    const isRecurring = (() => {
      const v: any = header?.recurring;
      if (typeof v === 'boolean') {
        return v;
      }
      if (typeof v === 'number') {
        return v === 1;
      }
      if (typeof v === 'string') {
        const s = v.trim().toLowerCase();
        return s === 'true' || s === '1' || s === 'y' || s === 'yes';
      }
      return false;
    })();

    const entryLabel = isRecurring ? 'Recurring Journal Entry' : 'Journal Entry';

    const headerHtml = `
      <div>
        <h1>${entryLabel} ${journalNumber}</h1>
        <div class="muted">${companyName}</div>
        <div class="muted">Date: ${dateStr}</div>
        <div class="muted">Recurring: ${isRecurring ? 'Yes' : 'No'}</div>
        ${locationName ? `<div class="muted">Location: ${locationName}</div>` : ''}
        ${rfLineHtml ? `<div class="muted">Reporting Fields: ${rfLineHtml}</div>` : ''}
      </div>
    `;

    const tableHtml = `
      <table>
        <thead>
          <tr>
            <th>#</th>
            <th>Account</th>
            <th>Description</th>
            <th>Location</th>
            <th>RFs</th>
            <th style="text-align:right">Debit</th>
            <th style="text-align:right">Credit</th>
          </tr>
        </thead>
        <tbody>
          ${rowsHtml}
        </tbody>
        <tfoot>
          <tr>
            <td colspan="5" class="totals">Total</td>
            <td style="text-align:right" class="totals" colspan="2">${Number.isFinite(total) ? (Math.abs(total)).toFixed(2) : ''}</td>
          </tr>
        </tfoot>
      </table>
    `;

    const headerDesc = header?.description || header?.desc || '';
    const headerDescHtml = headerDesc ? `<div style="margin-top:12px"><strong>Description:</strong><br>${String(headerDesc).replace(/\n/g, '<br>')}</div>` : '';

    const notes = header?.notes || header?.memo || '';
    const notesHtml = notes ? `<div style="margin-top:12px"><strong>Notes:</strong><br>${String(notes).replace(/\n/g, '<br>')}</div>` : '';

    const docTitle = `${companyName ? companyName + ' - ' : ''}${entryLabel} ${journalNumber}`;

    return `
      <html>
        <head>
          <meta charset="utf-8" />
          <title>${docTitle}</title>
          ${styles}
        </head>
        <body>
          ${headerHtml}
          ${headerDescHtml}
          ${tableHtml}
          ${notesHtml}
          <div class="no-print" style="margin-top:16px">
            <button onclick="window.print()">Print</button>
          </div>
        </body>
      </html>
    `;
  }

  // =====================
  // Unsaved-changes support (data-driven)
  // =====================
  buildSnapshot(overlayDomValues = true): any {
    try {
      // Start from current vm snapshot
      const item = this.vm?.item || {};
      const header = {...(item.header || {})} as any;
      const detail = Array.isArray(item.detail) ? item.detail.map((d: any) => ({...(d || {})})) : [];

      if (overlayDomValues) {
        // Overlay live DOM form control values (inputs/selects/textareas) by name
        try {
          const container = document.querySelector('.container-fluid.form.form-horizontal') || document;
          const controls = Array.from(container.querySelectorAll('input[name], select[name], textarea[name]')) as (HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement)[];
          for (const el of controls) {
            const name = (el.getAttribute('name') || '').trim();
            if (!name) {
              continue;
            }
            let value: any;
            if ((el as HTMLInputElement).type === 'checkbox') {
              value = (el as HTMLInputElement).checked;
            } else if ((el as HTMLInputElement).type === 'number') {
              const v = (el as HTMLInputElement).value;
              value = v === '' ? '' : Number(v);
            } else {
              value = (el as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement).value;
            }

            // Common header-bound fields (by name attribute)
            const normalize = (v: any) => (v === '' ? undefined : v);
            switch (name) {
              case 'description':
                header.description = normalize(value);
                break;
              case 'notes':
                header.notes = value;
                break;
              case 'currency_id':
                header.currency_id = normalize(value);
                break;
              case 'location_id':
                header.location_id = normalize(value);
                break;
              case 'company_id':
                if (value === '' && header.company_id) {
                  break;
                }
                header.company_id = normalize(value);
                break;
              case 'header_date':
                header.header_date = normalize(value);
                break;
              case 'post_date':
                header.post_date = normalize(value);
                break;
              case 'due_date':
                header.due_date = normalize(value);
                break;
              default:
                // Attempt to map detail inputs if they use a convention like name="detail[i].field"
              {
                const m = name.match(/^detail\[(\d+)\]\.([a-zA-Z0-9_]+)$/);
                if (m) {
                  const idx = Number(m[1]);
                  const field = m[2];
                  if (!isNaN(idx) && detail[idx]) {
                    (detail[idx] as any)[field] = normalize(value);
                  }
                }
                break;
              }
            }
          }
        } catch { /* non-fatal overlay */
        }
      }

      return {header, detail};
    } catch {
      return {};
    }
  }

  private setBaseline(): void {
    this.originalSnapshot = this.buildSnapshot(false);
    this.baselineReady = true;
  }

  private scheduleBaselineRefresh(): void {
    if (this.vm.entry_id === 'new') {
      return;
    }
    Promise.resolve().then(() => {
      try {
        if (this.vm?.item?.header?.id) {
          this.setBaseline();
        }
      } catch {
      }
    });
  }

  private sanitizeSnapshot(snap: any): any {
    try {
      const s = JSON.parse(JSON.stringify(snap || {}));
      const h = s.header || {};
      // Normalize and ignore pre-primed defaults and volatile totals/dates/numbers
      // Convert potential string numbers to numbers for stable compare
      try {
        if (h.detail_total !== undefined) {
          h.detail_total = Number(h.detail_total) || 0;
        }
        if (h.subtotal !== undefined) {
          h.subtotal = Number(h.subtotal) || 0;
        }
        if (h.tax_total !== undefined) {
          h.tax_total = Number(h.tax_total) || 0;
        }
        if (h.total !== undefined) {
          h.total = Number(h.total) || 0;
        }
        if (h.currency_rate !== undefined) {
          h.currency_rate = Number(h.currency_rate) || 0;
        }
      } catch {
      }

      //delete h.header_date;
      // delete h.post_date;
      // delete h.due_date;
      delete h.audit_sequence;
      delete h.number;
      delete h.date;
      delete h.detail_total;
      delete h.subtotal;
      delete h.tax_total;
      delete h.total;
      // Also ignore fields commonly updated post-save by server/business logic so Save Close won't prompt
      delete h.last_updated_at;
      delete h.updated_by;
      delete h.created_at;
      delete h.created_by;
      s.header = h;

      if (Array.isArray(s.detail)) {
        s.detail = s.detail.map((d: any) => {
          const x = {...(d || {})};
          delete x.accountInput;
          delete x.itemInput;
          delete x.amountInput;
          delete x.dc;
          if (x.amount === undefined && typeof x.debit_credit !== 'undefined') {
            x.amount = Number(x.debit_credit);
          }
          if (typeof x.amount !== 'undefined') {
            x.amount = Number(x.amount) || 0;
          }
          delete x.debit_credit;
          delete x.extended_amount;
          delete x.rf_id_0;
          delete x.rf_id_1;
          delete x.rf_id_2;
          delete x.rf_id_3;
          delete x.rf_id_4;
          delete x.rf_id_5;
          delete x.created_at;
          delete x.updated_at;
          // Remove volatile identifiers that can differ between loads
          delete x.id;
          delete x.line_id;
          delete x.temp_id;
          return x;
        });
        s.detail = s.detail.filter((d: any) => d && (d.account_id || d.amount || d.description));
        // Sort details by a stable composite key to avoid order-only diffs
        const keyOf = (d: any) => [
          String(d.account_id ?? ''),
          String(d.item_id ?? ''),
          String(d.description ?? ''),
          (typeof d.amount !== 'undefined' ? Number(d.amount) : 0)
        ].join('|');
        s.detail.sort((a: any, b: any) => keyOf(a).localeCompare(keyOf(b)));
      }
      return s;
    } catch {
      return snap || {};
    }
  }

  private logSnapshotDiff(context: string, current: any, original: any): void {
    try {
      const diffs: Array<{ path: string; current: any; original: any }> = [];
      const seen = new Set<any>();

      const isObject = (v: any) => v !== null && typeof v === 'object';

      const diffValues = (a: any, b: any, path: string) => {
        if (a === b) {
          return;
        }
        if (!isObject(a) || !isObject(b)) {
          diffs.push({path, current: a, original: b});
          return;
        }
        if (seen.has(a) || seen.has(b)) {
          return;
        }
        seen.add(a);
        seen.add(b);

        if (Array.isArray(a) || Array.isArray(b)) {
          const aArr = Array.isArray(a) ? a : [];
          const bArr = Array.isArray(b) ? b : [];
          const max = Math.max(aArr.length, bArr.length);
          if (aArr.length !== bArr.length) {
            diffs.push({path: `${path}.length`, current: aArr.length, original: bArr.length});
          }
          for (let i = 0; i < max; i += 1) {
            diffValues(aArr[i], bArr[i], `${path}[${i}]`);
          }
          return;
        }

        const keys = new Set<string>([...Object.keys(a), ...Object.keys(b)]);
        keys.forEach((key) => {
          diffValues(a[key], b[key], path ? `${path}.${key}` : key);
        });
      };

      diffValues(current, original, '');

      if (diffs.length === 0) {
        return;
      }

      const header = `[EntryForm] Unsaved-changes diff (${context})`;
      if (console.groupCollapsed) {
        console.groupCollapsed(header);
      } else {
      }
      if (console.groupEnd) {
        console.groupEnd();
      }
    } catch {
      // swallow logging errors
    }
  }

  hasChanges(): boolean {
    try {
      // Build sanitized snapshots for stable comparison
      const current = this.sanitizeSnapshot(this.buildSnapshot());
      const original = this.sanitizeSnapshot(this.originalSnapshot);
      const diff = JSON.stringify(current) !== JSON.stringify(original);

      // Edge case: user typed something in a new entry, then cleared it back to pristine.
      // If current equals original (no diff), ensure any cached error/state that could
      // influence prompts is cleared, and keep baseline aligned with the now-pristine state.
      if (!diff) {
        try {
          this.vm.error = 0;
          this.vm.errorDescription = '';
          // Keep baseline synced in case internal recalcs mutated transient fields earlier
          this.originalSnapshot = this.buildSnapshot(false);
        } catch {
        }
      }
      return diff;
    } catch {
      return false;
    }
  }

  // Allow route guard (if present) to query this component directly
  checkForChanges(): boolean {
    if (this.suppressGuardOnce) {
      this.suppressGuardOnce = false;
      return false;
    }
    return this.hasChanges();
  }

  logUnsavedChangesDiff(context: string = 'guard'): void {
    try {
      const current = this.sanitizeSnapshot(this.buildSnapshot());
      const original = this.sanitizeSnapshot(this.originalSnapshot);
      if (JSON.stringify(current) !== JSON.stringify(original)) {
        this.logSnapshotDiff(context, current, original);
      }
    } catch {
      // ignore logging failures
    }
  }

  isFieldEmpty(value: any): boolean {
    let retVal = false;
    if (!value) {
      retVal = true;
    } else {
      retVal = false;
    }
    return retVal;
  }

  init() {
    if (this.vm.entry_id === 'new') {
      //  NEW ENTRY
      // Build a minimal, stable skeleton first (no pre-primed defaults that will be overwritten by fetches)
      this.vm.item = {
        batchTypeId: this.vm.batchTypeId,
        batch: { type_id: this.vm.batchTypeId },
        header: {
          // Do NOT set company_id/location_id/currency_id/header_date/etc. here; those are primed later
          // Only include fields that the UI immediately binds and that won’t be overwritten by fetch
          detail_total: 0,
          // Establish a pristine baseline model for new entries so empty fields
          // are consistently compared as undefined (not empty string)
          description: undefined,
          notes: undefined,
          currency_id: undefined,
          location_id: undefined,
          company_id: undefined,
          header_date: undefined,
          post_date: undefined,
          due_date: undefined,
        },
        detail: [],
      };

      this.vm.entryTypeName = '';
      if (this.vm.recurring === 'true') {
        this.vm.item.header.recurring = this.vm.recurring;
      }
      if (this.vm.batchTypeId == '1') {
        const x = $localize`:Journal @@journal:Journal`;
        this.vm.entryTypeName = x;
      }
      if (this.vm.batchId) {
        this.vm.batchId = this.vm.item.batch.id = this.vm.batchId;
      } else {
        this.vm.item.batch = {
          type_id: this.vm.batchTypeId,
          description: 'Batch',
        };
      }
      // Defer priming of defaults until after lookups fetched and baseline captured
      // Initialize total formatted display for new entries (derived, ignored in comparator)
      this.updateTotalFormatted();
    } else {
      //  EDIT ENTRY
      if (this.vm.item.batch.type_id == '1') {
        this.vm.entryTypeName = $localize`:Journal @@journal:Journal`;
        this.isExistingEntry = true;
      }

      for (const detail of this.vm.item.detail) {
        if (detail.item_id) {
          if (
            this.FilterObject(
              this.vm.inventoryItem,
              { id: detail.item_id },
              true
            )
          ) {
            detail.itemInput = this.FilterObject(
              this.vm.inventoryItem,
              { id: detail.item_id },
              true
            )[0].code;
          }
        }
        if (this.vm.account) {
          if (
            this.FilterObject(this.vm.account, { id: detail.account_id }, true)
              .length > 0
          ) {
            detail.accountInput =
              this.FilterObject(
                this.vm.account,
                { id: detail.account_id },
                true
              )[0].number +
              ' ' +
              this.FilterObject(
                this.vm.account,
                { id: detail.account_id },
                true
              )[0].name;
          }
          detail.amount = Number(detail.amount);
          detail.quantity = Number(detail.quantity);
          detail.debit_credit = Number(detail.debit_credit);
          if (detail.debit_credit > 0) {
            detail.amountInput = this.formatAmountWithSeparators(Number(detail.debit_credit));
            detail.dc = '1';
          } else {
            detail.amountInput = this.formatAmountWithSeparators(0 - Number(detail.debit_credit));
            detail.dc = '-1';
          }
        }
        //  vm.item.detail[x].sort_order = 0;
      }
      this.calcTotal();

      // Rebind account display fields if accounts are already loaded; otherwise queue it
      if (this.accountsReady) {
        // schedule after current stack to avoid interfering with other initializations
        Promise.resolve().then(() => this.rebindAccountInputs());
      } else {
        this.pendingRebindAccounts = true;
      }

      // Ensure RF is expanded on load if RF values exist on any detail line
      try {
        for (const d of this.vm.item.detail) {
          const hasVal = (x: any) => x !== undefined && x !== null && x !== '';
          (d as any).showRf = !!(hasVal(d.rf_id_1) || hasVal(d.rf_id_2) || hasVal(d.rf_id_3) || hasVal(d.rf_id_4) || hasVal(d.rf_id_5));
        }
      } catch {
      }
    }
    if (this.vm.item.detail) {
      for (const detail of this.vm.item.detail) {
        if (detail.debit_credit) {
          detail.debit_credit = Number(detail.debit_credit).toFixed(2);
        }
      }
    }

    if (this.vm.company && this.vm.company.length === 1 && !this.vm.item.header.company_id) {
      this.vm.item.header.company_id = this.vm.company[0].id;
    }

    if (this.vm.currency && this.vm.currency.length === 1) {
      this.vm.item.header.currency_id = this.vm.currency[0].id;
    }
    if (this.vm.location && this.vm.location.length === 1) {
      this.vm.item.header.location_id = this.vm.location[0].id;
    }
    if (this.vm.account && this.vm.account.length) {
      this.createAccountName();
    }
    this.addRows(2); //  create initial detail row

    this.vm.isLoaded = true;
    // // Baseline original snapshot BEFORE priming defaults that are not user-entered
    // try { this.originalSnapshot = this.buildSnapshot(); } catch {}

    // Now safely prime defaults that come from preferences or single-option lookups.
    // These are ignored by the comparator but are required for UX. Doing it after the baseline
    // avoids transient diffs during first navigation attempts.
    if (this.vm.Preferences?.default_company_id && !this.vm.item.header.company_id) {
      this.vm.item.header.company_id = this.vm.Preferences.default_company_id;
    }
    if (this.vm.company && this.vm.company.length === 1 && !this.vm.item.header.company_id) {
      this.vm.item.header.company_id = this.vm.company[0].id;
    }
    if (this.vm.currency && this.vm.currency.length === 1) {
      this.vm.item.header.currency_id = this.vm.currency[0].id;
    }
    if (this.vm.location && this.vm.location.length === 1) {
      this.vm.item.header.location_id = this.vm.location[0].id;
    }
    // Baseline original snapshot BEFORE priming defaults that are not user-entered
    try {
      if (this.vm.entry_id === 'new') {
        this.setBaseline();
      } else {
        this.scheduleBaselineRefresh();
      }
    } catch {
    }
  }

  //   ACCOUNTS
  private getAccountNameList(): any[] {
    const existing = this.vm?.accountName;
    if (Array.isArray(existing) && existing.length > 0) {
      return existing;
    }

    const list: any[] = Array.isArray(this.vm?.account) ? this.vm.account : [];
    const names = list
      .map((account) => {
        const number = account?.number ?? '';
        const name = account?.name ?? '';
        const label = `${number} ${name}`.trim();
        return label ? {name: label} : null;
      })
      .filter(Boolean);

    return names as any[];
  }

  createAccountName() {
    this.vm.accountName = [];
    let tempName = '';

    // if(this.vm.account.length){
    for (const account of this.vm.account) {
      tempName = account.number + ' ' + account.name;
      this.vm.accountName.push({
        name: tempName,
      });
    }
    // }
  }

  /** Rebuilds a quick lookup map for accounts by id to avoid O(n) scans */
  private rebuildAccountIndex(): void {
    this.accountById = {};
    try {
      const list: any[] = Array.isArray(this.vm.account) ? this.vm.account : [];
      for (const a of list) {
        const key = String(a?.id ?? '').trim();
        if (key) {
          this.accountById[key] = a;
        }
      }
    } catch {
      this.accountById = {};
    }
  }

  /**
   * For existing entries where detail lines are loaded before accounts,
   * rebind the account display fields once the account list is available.
   */
  private rebindAccountInputs(): void {
    if (!this.accountsReady) {
      this.pendingRebindAccounts = true;
      return;
    }
    this.pendingRebindAccounts = false;
    try {
      const details: any[] = this.vm?.item?.detail || [];
      let invalidFound = false;
      for (const d of details) {
        const id = String(d?.account_id ?? '').trim();
        if (!id) {
          continue;
        }
        const acc = this.accountById[id];
        if (acc) {
          d.accountInput = `${acc.number} ${acc.name}`;
        } else {
          invalidFound = true;
          d.accountInput = '';
          d.account_id = undefined;
        }
      }
      if (invalidFound) {
        this.notification.showWarningToast(
          $localize`:Account not valid @@accountnotvalid:Account not valid`
        );
      }
      this.scheduleBaselineRefresh();
    } catch {
      // no-op
    }
  }

  updateAccount() {
    this.vm.startAccount = 0;
    // this.localStorage.StorageGet('account', '');
    this.tableDataService
      .getApiListData('account', '?state=active&pageNumber=0&pageSize=999999')
      .subscribe((accountResult: any) => {
        this.vm.account = accountResult.Data;
        this.createAccountName();
        this.rebuildAccountIndex();
        this.accountsReady = true;
        this.rebindAccountInputs();
      });
  }

  selectAccountModal(accountId) {
    //  let id = document.getElementById('accountSelectDetailId').value;
    const id = this.vm.tempAccountDetailId;
    for (const detail of this.vm.item.detail) {
      if (detail.id == id) {
        for (const account of this.vm.account) {
          if (account.id == accountId) {
            detail.account_id = account.id;
            detail.accountInput = account.number + ' ' + account.name;
            break;
          }
        }
        break;
      }
    }
  }

  accountSelected(detailOrId: any, inputValue?: string) {
    const detail =
      detailOrId && typeof detailOrId === 'object'
        ? detailOrId
        : this.vm.item.detail.find(d => d.id == detailOrId);

    if (!detail) {
      return;
    }

    const rawInput = String(inputValue ?? detail.accountInput ?? '').trim();
    if (rawInput.length === 0) {
      detail.account_id = undefined;
      return;
    }

    const accountId = detail.account_id;
    let matchedAccount = this.vm.account.find(a => a.id == accountId);

    if (!matchedAccount) {
      const normalizedInput = rawInput.replace(/\s+/g, ' ').toLowerCase();

      for (const account of this.vm.account) {
        const fullName = account.number + ' ' + account.name;
        const normalizedFull = String(fullName).trim().replace(/\s+/g, ' ').toLowerCase();
        const normalizedName = String(account.name).trim().replace(/\s+/g, ' ').toLowerCase();
        const normalizedNumber = String(account.number).trim().replace(/\s+/g, ' ').toLowerCase();

        if (
          normalizedFull === normalizedInput ||
          normalizedName === normalizedInput ||
          normalizedNumber === normalizedInput
        ) {
          matchedAccount = account;
          break;
        }
      }
    }

    if (matchedAccount) {
      detail.account_id = matchedAccount.id;
      detail.accountInput = `${matchedAccount.number} ${matchedAccount.name}`.trim();
    } else {
      this.notification.showWarningToast(
        $localize`:Account not valid @@accountnotvalid:Account not valid`
      );
      detail.accountInput = '';
      detail.account_id = undefined;
    }
  }

  saveNewAccount() {
    const errorDescription = '';
    this.vm.error = 0;
    this.vm.accountButtonsDisabled = true;

    if (!this.vm.newAccount.name) {
      this.notification.showWarningToast(
        $localize`:Name is required. @@nameisrequired:Name is required.`
      );
      this.vm.newAccountNameError = true;
      this.vm.error = 1;
    } else if (
      this.FilterObject(
        this.vm.account,
        { name: this.vm.newAccount.name },
        true
      ).length > 0
    ) {
      if (
        this.FilterObject(
          this.vm.account,
          { name: this.vm.newAccount.name },
          true
        )[0].name == this.vm.newAccount.name
      ) {
        this.vm.newAccountNameError = true;
        this.notification.showErrorToast(
          $localize`:Name is a duplicate. @@nameisaduplicate:Name is a duplicate.`
        );
        this.vm.error = 1;
      }
    }
    if (!this.vm.newAccount.number) {
      this.notification.showErrorToast(
        $localize`:Number is required. @@numberisrequired:Number is required.`
      );
      this.vm.newAccountNumberError = true;
      this.vm.error = 1;
    } else if (
      this.FilterObject(
        this.vm.account,
        { name: this.vm.newAccount.number },
        true
      ).length > 0
    ) {
      if (
        this.FilterObject(
          this.vm.account,
          { name: this.vm.newAccount.number },
          true
        )[0].number == this.vm.newAccount.number
      ) {
        this.vm.newAccountNumberError = true;
        this.notification.showWarningToast(
          $localize`:Number is a duplicate. @@numberisaduplicate:Number is a duplicate.`
        );
        this.vm.error = 1;
      }
    }
    if (this.vm.error == 1) {
      return;
    }
    if (errorDescription === '') {
      this.tableDataService.post('account', this.vm.newAccount).subscribe(
        (item: any) => {
          // this.notification.success($localize`:Saved@@saved:Saved`);
          this.vm.accountButtonsDisabled = false;
          this.updateAccount();
          this.vm.newAccount = {};
          //  this.createAccountName();
        },
        (error: any) => {
          this.vm.accountButtonsDisabled = false;
          // this.notification.error(error.description);
        }
      );
    } else {
      //  report error
      this.vm.accountButtonsDisabled = false;
      // this.notification.error(errorDescription);
    }
  }

  //  UTILITIES

  addRows(qty) {
    if (qty == '') {
      qty = 1;
    }
    let maxSortOrder = 0;
    for (const detail of this.vm.item.detail) {
      if (maxSortOrder < detail.sort_order) {
        maxSortOrder = detail.sort_order;
      }
    }
    for (let x = 1; x <= qty; x++) {
      //  tempID =this.vm.item.detail.length + 1;
      const tempDate = new Date();
      const tempID =
        tempDate.getMinutes() *
          (tempDate.getSeconds() + tempDate.getMilliseconds()) +
        this.vm.item.detail.length;
      maxSortOrder++;
      this.vm.item.detail.push({
        id: tempID,
        quantity: 1,
        sort_order: maxSortOrder,
      });
    }
  }

  deleteLine(id) {
    for (let x = 0; x < this.vm.item.detail.length; x++) {
      if (this.vm.item.detail[x].id == id) {
        this.vm.item.detail.splice(x, 1);
        this.calcTotal();
      }
    }
  }

  changeToDecimal(event: any) {
    event = parseFloat(event);
    return event.toFixed(2);
  }

  // Strip common thousand separators and spaces from a value -> string of numeric form
  private stripSeparators(value: any): string {
    if (value === null || value === undefined) {
      return '';
    }
    const s = String(value);
    return s.replace(/,/g, '').replace(/\s+/g, '');
  }

  // Format a number (or numeric string) to 1,234.56 style with two decimals
  private formatAmountWithSeparators(value: any): string {
    const n = Number(this.stripSeparators(value));
    if (!Number.isFinite(n)) {
      return '';
    }
    const fixed = Math.abs(n).toFixed(2);
    const [intPart, decPart] = fixed.split('.');
    const withCommas = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return `${withCommas}.${decPart}`;
  }

  onAmountFocus(detail: any): void {
    try {
      if (!detail) {
        return;
      }
      if (detail.amountInput === '-' || detail.amountInput === '') {
        return;
      }
      detail.amountInput = this.stripSeparators(detail.amountInput);
    } catch {
    }
  }

  onAmountBlur(detail: any): void {
    try {
      if (!detail) {
        return;
      }
      // Normalize and format for display
      const raw = this.stripSeparators(detail.amountInput);
      const num = Number(raw);
      if (Number.isFinite(num)) {
        // Keep absolute value in amountInput; sign is controlled by dc
        detail.amountInput = this.formatAmountWithSeparators(Math.abs(num));
      } else {
        detail.amountInput = '';
      }
      // Recalculate totals after formatting
      this.calcTotal();
    } catch {
      this.calcTotal();
    }
  }

  calcTotal() {
    let total = 0;
    for (const detail of this.vm.item.detail) {
      detail.debit_credit = 0;
      if (detail.amountInput && detail.dc) {
        const raw = this.stripSeparators(detail.amountInput);
        const amt = Number(raw);
        const dc = Number(detail.dc);
        if (Number.isFinite(amt) && (dc === 1 || dc === -1)) {
          detail.debit_credit = Math.round(amt * dc * 100) / 100;
          total += detail.debit_credit;
        } else if (raw !== '-' && raw !== '') {
          this.notification.showErrorToast(
            $localize`:Amount @@amount:Amount` +
              ' ' +
            detail.amountInput +
              ' ' +
              $localize`:is not a number, please remove any text. @@isnotanumberpleaseremoveanytext:is not a number, please remove any text.`
          );
        }
      }
    }
    this.vm.total = Math.round(total * 100) / 100;
    // Update formatted total string for display without affecting numeric comparisons
    this.updateTotalFormatted();
  }

  // Update the formatted Balance total string (thousands separators, 2 decimals) based on numeric vm.total
  private updateTotalFormatted(): void {
    const t: any = (this as any)?.vm?.total;
    const num = (t === undefined || t === null || t === '') ? 0 : Number(t);
    this.vmTotalFormatted = this.formatNumberWithSeparatorsSigned(num);
  }

  // Format a signed number with thousands separators and 2 decimals (e.g., -1,234.56)
  private formatNumberWithSeparatorsSigned(value: any): string {
    const n = Number(value);
    if (!Number.isFinite(n) || Number.isNaN(n)) {
      return '0.00';
    }
    const sign = n < 0 ? '-' : '';
    const abs = Math.abs(n);
    const fixed = abs.toFixed(2);
    const [intPart, decPart] = fixed.split('.');
    const withCommas = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return `${sign}${withCommas}.${decPart}`;
  }

  print() {
    this.vm.action = 'print';
    this.vm.save();
  }

  async save() {
    this.vm.errorDescription = '';
    this.submitted = true;

    //  check errors
    this.vm.error = 0;
    if (!this.vm.item.header.header_date) {
      // this.notification.showErrorToast(
      //   $localize`:Date is required. @@dateisrequired:Date is required.`
      // );
      this.vm.error = 1;
      this.vm.dateMissing = true;
    } else {
      const matchedCompany = this.FilterObject(
        this.vm.company,
        { id: this.vm.item.header.company_id },
        true
      )[0];
      if (matchedCompany) {
        const yearEndDate = matchedCompany.year_end_date;
        const cutOffDate = matchedCompany.cut_off_date;
        const tempYearEndDate = new Date(yearEndDate);
        const tempCutOffDate = new Date(cutOffDate);
        const headerDate = new Date(this.vm.item.header.header_date);
        // console.log(this.deleteMode);
        if (headerDate < tempYearEndDate && !this.deleteMode) {
          this.notification.showWarningToast(
            $localize`:Date must be greater than the the year end @@datemustbegreaterthantheyearend:Date must be greater than the year end` +
            ' ' +
            yearEndDate
          );
          this.vm.error = 1;
        }
        if (headerDate < tempCutOffDate && !this.deleteMode) {
          this.notification.showWarningToast(
            $localize`:Date must be greater than the cut off date @@datemustbegreaterthanthecutoffdate:Date must be greater than the cut off date` +
            ' ' +
            cutOffDate
          );
          this.vm.error = 1;
        }
      }
      this.vm.item.header.header_date = this.dateFilter.GetDateFormat(
        this.vm.item.header.header_date
      );
    }

    // Normalize reversal_date to ISO string if reversal is requested
    if (this.vm.item.header.create_reversal && this.vm.item.header.reversal_date) {
      this.vm.item.header.reversal_date = this.dateFilter.GetDateFormat(
        this.vm.item.header.reversal_date
      );
    } else if (!this.vm.item.header.create_reversal) {
      this.vm.item.header.reversal_date = null;
    }

    // Normalize recurring_end_date to ISO string if recurring; clear if not recurring
    if (this.vm.item.header.recurring && this.vm.item.header.recurring_end_date) {
      this.vm.item.header.recurring_end_date = this.dateFilter.GetDateFormat(
        this.vm.item.header.recurring_end_date
      );
    } else if (!this.vm.item.header.recurring) {
      this.vm.item.header.recurring_end_date = null;
    }

    if (this.vm.item.header.create_reversal && !this.vm.item.header.reversal_date) {
      this.notification.showWarningToast(
        $localize`:Reverse Date is required when Reverse is selected. @@reversaldateisrequired:Reverse Date is required when Reverse is selected.`
      );
      this.vm.error = 1;
    }

    if (this.vm.item.header.recurring && !this.vm.item.header.recurring_end_date) {
      this.notification.showWarningToast(
        $localize`:Recurring End Date is required when Recurring is selected. @@recurringenddateisrequired:Recurring End Date is required when Recurring is selected.`
      );
      this.vm.error = 1;
    }

    // Normalize intercompany fields
    if (!this.vm.item.create_intercompany) {
      this.vm.item.intercompany_company_id = null;
      this.vm.item.intercompany_location_id = null;
    }
    if (this.vm.item.create_intercompany && !this.vm.item.intercompany_company_id) {
      this.notification.showWarningToast(
        $localize`:To Company is required when Intercompany is selected. @@tocompanyisrequired:To Company is required when Intercompany is selected.`
      );
      this.vm.intercompanyError = true;
      this.vm.error = 1;
    }
    if (this.vm.item.create_intercompany && !this.vm.item.intercompany_location_id) {
      this.notification.showWarningToast(
        $localize`:To Location is required when Intercompany is selected. @@tolocationisrequired:To Location is required when Intercompany is selected.`
      );
      this.vm.intercompanyLocationError = true;
      this.vm.error = 1;
    }

    if (!this.vm.item.header.company_id) {
      this.notification.showWarningToast(
        $localize`:Company is required. @@companyisrequired:Company is required.`
      );
      this.vm.companyError = true;
      this.vm.error = 1;
    }

    if (!this.vm.item.header.description) {
      this.notification.showWarningToast(
        $localize`:Must enter Description. @@mustEnterDescription :Must enter Description.`
      );
      this.vm.descriptionError = true;
      this.vm.error = 1;
    }

    //  if (!this.vm.item.header.currency_id) {
    //     this.vm.errorDescription += 'Currency is required.<br/>';
    //     this.vm.currencyError = true;
    //  }
    // console.log(JSON.stringify(this.vm.item.detail));
    // console.log('length: ' + this.vm.item.detail[1].length);

    if (this.vm.item.detail[1].amountInput === undefined && !this.deleteMode) {
      this.notification.showWarningToast(
        $localize`:Must at least one full entry. @@mustbeatleastonefull entry:Must at least one full entry.`
      );
      this.vm.error = 1;
    }


    for (const detail of this.vm.item.detail) {
      if (detail.debit_credit !== 0 && detail.debit_credit && !this.deleteMode) {
        // console.log(detail.debit_credit);
        if (!detail.account_id && detail.debit_credit ) {
          this.notification.showWarningToast(
            $localize`:Detail account is required. @@detailaccountisrequired:Detail account is required.`
          );
          this.vm.error = 1;
        }
      }
    }

    if (this.vm.total !== 0 && !this.deleteMode) {
      this.notification.showWarningToast(
        $localize`:Entry must be balanced. @@entrymustbebalanced:Entry must be balanced.`
      );
      this.vm.error = 1;
    }

    if (this.vm.error == 1) {
      return;
    }

    // if we got this far entry is balanced so just add the positive side of balanced entry to get total
    // for entry value display.

    // clear so it recalcs number
    this.vm.item.header.detail_total = 0;

    for (const detail of this.vm.item.detail) {
      if (detail.amount > 0) {
        this.vm.item.header.detail_total += Number(detail.amount);
      }
      this.vm.item.header.detail_total = this.changeToDecimal(this.vm.item.header.detail_total);
    }

    if (this.vm.errorDescription === '') {
      // console.log('post data: ' + JSON.stringify(this.vm.item));
      if (this.vm.item.header.id === undefined) {
        //  new entry
        // this.vm.item.header.audit_sequence = 0;
        this.tableDataService.post('entry', this.vm.item).subscribe({
          next: (item: any) => {
            let apiReturn;
            if (this.saveCloseUsed) {
              this.saveCloseUsed = false;
              if (this.vm.recurring === 'true') {
                this.router.navigateByUrl('/transactions/entry/recurring');
              } else {
                this.router.navigateByUrl('/transactions/entry/list');
              }
              return;
            }

            if (typeof item === 'object') {
              apiReturn = JSON.parse(JSON.stringify(item));
              // Reset baseline to the just-saved state prior to navigation
              try {
                this.setBaseline();
              } catch {
              }
              if (this.vm.recurring === 'true') {
                this.router.navigateByUrl(
                  '/transactions/entry/form?entryId=' + apiReturn.id + '&recurring=' + this.vm.recurring
                );
              } else {
                this.router.navigateByUrl(
                  '/transactions/entry/form?entryId=' + apiReturn.id + '&recurring=' + this.vm.recurring
                );
              }
              this.pageLoadedFromPost = true;
            } else {
              if (this.vm.recurring === 'true') {
                this.router.navigateByUrl('/transactions/entry/recurring');
              } else {
                this.router.navigateByUrl('/transactions/entry/list');
              }
            }
          },
          error: (error: any) => {
            console.log(error);
            this.vm.buttonsDisabled = false;
            if (error.message) {
              this.notification.showErrorToast(error.message);
            }
          }
        });
      } else {
        // edit
        const changes: any = {};

        changes.detail = this.vm.item.detail;
        changes.salesperson = this.vm.item.salesperson;
        changes.header = this.vm.item.header;
        changes.create_reversal = this.vm.item.header.create_reversal;
        changes.reversal_date = this.vm.item.header.reversal_date ?? null;
        changes.create_intercompany = this.vm.item.create_intercompany;
        changes.intercompany_company_id = this.vm.item.intercompany_company_id ?? null;
        changes.intercompany_location_id = this.vm.item.intercompany_location_id ?? null;
        await this.tableDataService
          .patch('entry', this.vm.item.header.id, changes)
          .then(
            (item: any) => {
              this.vm.buttonsDisabled = false;
              // this.notification.success(item.description);
              if (this.vm.item.header.deleted) {
                this.onClose();
              } else {
                this.pageLoadedFromPost = true;

                if (this.saveCloseUsed) {
                  if (this.vm.recurring === 'true') {
                    this.router.navigateByUrl('/transactions/entry/recurring');
                  } else {
                    this.router.navigateByUrl('/transactions/entry/list');
                  }
                } else {
                  // Reset baseline to the just-saved state prior to navigation
                  try {
                    this.setBaseline();
                  } catch {
                  }
                  this.router.navigateByUrl(
                    '/transactions/entry/form?entryId=' +
                    this.vm.item.header.id +
                    '&thisBatchId=&recurring=' + this.vm.recurring);
                }
              }
            },
            (error: any) => {
              console.log(error);
              this.vm.buttonsDisabled = false;
              if (error.message) {
                this.notification.showErrorToast(error.message);
              }
            }
          );
      }
    } else {
      //  report error
      this.vm.buttonsDisabled = false;
      this.notification.showErrorToast(this.vm.errorDescription);
    }

    if (this.vm.item.header.id === 0) {
      this.onClose(); // close it so the entry is saved - should change this to just reset value from api return for id
    }
  }

  generateEntryCsv(clear: boolean) {
    let maxSortOrder = 0;

    this.vm.clearEntry = clear;

    if (this.vm.clearEntry) {
      this.vm.item.detail = [];
      this.vm.clearEntry = false;
    }

    for (const detail of this.vm.item.detail) {
      if (maxSortOrder < detail.sort_order) {
        maxSortOrder = detail.sort_order.sort_order;
      }
    }

    for (let x = 0; x < this.vm.csvResults.length; x++) {
      if (this.vm.csvResults[x].account) {
        let tempAccountId = '';
        let tempAccountInput = '';
        let tempLocationId = '';
        for (const account of this.vm.account) {
          const tempAccount = this.vm.csvResults[x].account;
          const indexOfSpace = tempAccount.indexOf(' ');
          const tempAccountNumber = tempAccount.substring(0, indexOfSpace);
          if (
            account.number + ' ' + account.name ==
            this.vm.csvResults[x].account
          ) {
            tempAccountInput = this.vm.csvResults[x].account;
            tempAccountId = account.id;
            break;
          } else if (account.number == tempAccountNumber) {
            tempAccountInput = account.number + ' ' + account.name;
            tempAccountId = account.id;
            break;
          }
        }
        if (this.vm.csvResults[x].location) {
          for (const location of this.vm.location) {
            if (location.name == this.vm.csvResults[x].location) {
              tempLocationId = location.id;
              break;
            }
          }
        }
        for (const account of this.vm.account) {
          if (account.number == this.vm.csvResults[x].account) {
            tempAccountInput =
              this.vm.csvResults[x].account + ' ' + account.name;
            tempAccountId = account.id;
            break;
          }
        }
        let tempAmount = 0;
        let tempDebitCredit = 0;
        let dc1 = '1';
        if (this.vm.csvResults[x].debit > 0) {
          tempAmount = Number(this.vm.csvResults[x].debit);
          tempDebitCredit = tempAmount;
        } else {
          tempAmount = Number(this.vm.csvResults[x].credit);
          dc1 = '-1';
          tempDebitCredit = 0 - tempAmount;
        }
        if (tempAmount != 0 && this.vm.csvResults[x].account != ' ') {
          this.vm.item.detail.push({
            id: x,
            accountInput: tempAccountInput,
            account_id: tempAccountId,
            amount: tempAmount,
            amountInput: tempAmount,
            debit_credit: tempDebitCredit,
            dc: dc1,
            quantity: 1,
            sort_order: x + maxSortOrder,
            location_id: tempLocationId,
            description: this.vm.csvResults[x].description,
          });
        }
      }
    }
    this.calcTotal();
    // show import buttons since we have valid data
    this.showImportButtons = true;
  }

  gotAnUploadFile(file: any) {
    // console.log('in got a file: ' + file);
    // console.log('in got a file: ');
  }

  syncItemAttachments(attachmentsArray: any) {
    // console.log('attachments array recvd in main');
    // console.log(attachmentsArray);

    // clear existing - will be put back because in array passed back in file upload array
    this.vm.item.attachments = [];

    // use the pieces needed for the api record
    attachmentsArray.forEach((x) => {
      this.vm.item.attachments.push({
        id: x.id,
        header_id: x.header_id,
        file_name: x.fileName,
        type: x.fileType,
        attachment: x.fileData,
        audit_sequence: 0
      });
    });
    // console.log('main vm array after sync to file upload data');
    // console.log(this.vm);
  }

  FilterObject(value, filterObj, except = false) {
    // console.log('filer: ' + value);
    return new FilterPipe().transform(value, filterObj);
  }

  uploadListener(event): void {
    const result = [];
    const files = event.srcElement.files;

    // Check if any file was selected
    if (!files || !files.length) {
      this.notification.showWarningToast('Please select a file to upload');
      return;
    }

    const file = files[0];

    // Check file format/extension
    if (!this.isValidCSVFile(file)) {
      this.notification.showWarningToast(
        $localize`:Please import valid .csv file. @@pleaseimportvalidcsvfile:Please import valid .csv file.`
      );
      this.csvReader.nativeElement.value = '';
      this.vm.csvResults = {};
      return;
    }

    // Check file size (e.g. max 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB in bytes
    if (file.size > maxSize) {
      this.notification.showWarningToast('File size exceeds 5MB limit');
      this.csvReader.nativeElement.value = '';
      this.vm.csvResults = {};
      return;
    }

    const input = event.target;
    const reader = new FileReader();
    let headers = [];
    reader.readAsText(file);

    reader.onload = () => {
      const csvData = reader.result;
      const rows = (csvData as string).split(/\r\n|\n/);

      // Check if file has content
      if (!rows || !rows.length) {
        this.notification.showWarningToast('File appears to be empty');
        return;
      }

      // Check if file has headers
      const headerRow = rows[0];
      if (!headerRow || !headerRow.trim()) {
        this.notification.showWarningToast('File must contain column headers');
        return;
      }

      // Check for required columns
      const requiredColumns = ['account', 'debit', 'credit']; // Add your required columns
      const headerCells = headerRow.toLowerCase().split(',');
      const missingColumns = requiredColumns.filter(col => !headerCells.includes(col));

      if (missingColumns.length > 0) {
        this.notification.showWarningToast(
          `Missing required columns: ${missingColumns.join(', ')}`
        );
        return;
      }

      // Process the file if all validations pass
      for (let i = 0; i < rows.length; i++) {
        if (i == 0) {
          headers = rows[i].split(',');
          for (let j = 0; j < headers.length; j++) {
            headers[j] = headers[j].toLowerCase();
            headers[j] = headers[j].replace('\r', '');
            headers[j] = headers[j].trimEnd();
          }
        } else {
          const obj = {};
          const cells = rows[i].split(',');

          // Skip empty rows
          if (cells.join('').trim() === '') {
            continue;
          }

          for (let j = 0; j < cells.length; j++) {
            cells[j] = cells[j].replace('\r', '');
            obj[headers[j]] = cells[j];
          }
          result.push(obj);
        }
      }

      if (result.length > 0) {
        this.vm.csvResults = result;
        this.showImportButtons = true;
        this.notification.showSuccessToast('Entries Imported For Review');
      } else {
        this.notification.showWarningToast('Problem Importing Entries - No Valid Data Found');
      }
    };

    reader.onerror = () => {
      this.notification.showErrorToast(
        $localize`:error is occured while reading file! @@errorisoccuredwhilereadingfile:error is occured while reading file!`
      );
    };
  }

  isValidCSVFile(file: any) {
    return file.name.endsWith('.csv');
  }

  onClose() {
    // Close should ONLY prompt when there are real edits. Perform a quick compare; if no diff,
    // navigate directly without invoking guard. If diff exists, navigate normally and allow guard to prompt.
    let hasDiff = false;
    try {
      const current = this.sanitizeSnapshot(this.buildSnapshot());
      const original = this.sanitizeSnapshot(this.originalSnapshot);
      hasDiff = JSON.stringify(current) !== JSON.stringify(original);
      if (hasDiff) {
        this.logSnapshotDiff('onClose', current, original);
      }
    } catch { /* ignore, fall back to guard */
    }

    const fallback = (this.vm.recurring === 'true')
      ? '/transactions/entry/recurring'
      : '/transactions/entry/list';

    if (!hasDiff) {
      (this as any).nav?.backOr?.(fallback) ?? this.router.navigateByUrl(fallback).then();
    } else {
      // Let guard prompt
      this.router.navigateByUrl(fallback).then();
    }
  }

  onSaveClose() {
    // Per updated requirement: do not guard Save Close; always save and then close.
    this.saveCloseUsed = true;
    this.suppressGuardOnce = true;
    this.save();
  }

  async onSaveNew() {
    // Save current then navigate to a brand-new form; no guard/prompt.
    await this.save();
    await this.router.navigateByUrl('/transactions/entry/form?entryId=new');
  }

  async onClone() {
    // Do NOT save current entry; create a new entry prefilled from current form values
    try {
      // Build from live snapshot to capture any unsaved edits
      const snap = this.buildSnapshot();
      const cloned: any = {
        ...(this.vm.item || {}),
        header: {...(this.vm?.item?.header || {})},
        detail: Array.isArray(this.vm?.item?.detail) ? JSON.parse(JSON.stringify(this.vm.item.detail)) : []
      };
      // Overlay live header fields from snapshot
      Object.assign(cloned.header, snap.header || {});
      // Replace details with live ones if snapshot provided
      if (Array.isArray(snap.detail)) {
        cloned.detail = JSON.parse(JSON.stringify(snap.detail));
      }
      // Filter out empty detail lines
      cloned.detail = (cloned.detail || []).filter((d: any) => d && (d.account_id || d.amount || d.description));
      if (!cloned.header) {
        cloned.header = {};
      }
      // Clear identifiers and numbers per requirements
      cloned.header.id = undefined;
      // Ensure Number is empty (not 0). Clear both number and audit_sequence.
      cloned.header.number = undefined;
      cloned.header.audit_sequence = undefined;
      // Clear persisted-only flags that could interfere
      cloned.header.deleted = undefined;
      // Recalculate header totals based on details for balance prior to first save
      try {
        const sum = (Array.isArray(cloned.detail) ? cloned.detail.reduce((acc: number, d: any) => acc + (Number(d?.amount ?? d?.debit_credit ?? 0) || 0), 0) : 0);
        cloned.header.detail_total = Number(sum) || 0;
      } catch {
        cloned.header.detail_total = 0;
      }
      // Navigate to new form carrying the cloned data in navigation state
      await this.router.navigate(['/transactions/entry/form'], {
        queryParams: {entryId: 'new'},
        state: {clonedItem: cloned}
      });
    } catch {
      // Fallback: just open a new entry if cloning state cannot be built
      await this.router.navigateByUrl('/transactions/entry/form?entryId=new');
    }
  }

  deleteItem() {
    this.deleteMode = true;
    if (this.vm.entry_id != 'new') {
      const brClass = this;
      // let retMsg = '';
      const messages: string[] = [
        $localize`:Are you sure you want to delete record? @@areyousureyouwanttodeleterecord?:Are you sure you want to delete record?`,
        '',
      ];
      this.confirmDialogService.confirmThis(
        messages,
        () => {
          this.suppressGuardOnce = true;
          this.vm.item.header.deleted = true;
          // this.save().then(() => { this.onClose(); });
          // this.save().then();
          this.deleteEntry();
          this.deleteMode = false;
        },
        () => {
          brClass.notification.showSuccessToast(
            $localize`:Delete Canceled @@deleteCanceled:Delete Canceled`
          );
        }
      );
    }
  }

  private deleteEntry() {
    this.tableDataService.deleteDataById('entry', this.vm.entry_id).subscribe(
      (item: any) => {
        this.onClose();
      },
      (error: any) => {
        console.log(error);
        const msg = error?.error?.message || error?.message || 'Delete failed. If this entry has a reversing entry, please delete the reversing entry first.';
        this.notification.showWarningToast(msg);
      }
    );
  }


  private fetchLookupData(): void {
    const ACTIVE_ALL = `?${this.STATE_ACTIVE}&pageNumber=0&pageSize=999999`;
    const ALL = this.PAGE_ALL;

    forkJoin({
      account: this.tableDataService.getApiListData('account', ACTIVE_ALL),
      location: this.tableDataService.getApiListData('location', ACTIVE_ALL),
      accountSubtotal: this.tableDataService.getApiListData('account_subtotal_no_parents', ALL),
      company: this.tableDataService.getApiListData('company', ACTIVE_ALL),
      currency: this.tableDataService.getApiListData('currency', ACTIVE_ALL),
      rfType: this.tableDataService.getApiListData(this.API_REPORTING_FIELD_TYPE, ALL),
      reportingFields: this.tableDataService.getApiListData(this.API_REPORTING_FIELD, ACTIVE_ALL),
    }).subscribe({
      next: (res: any) => {
        try {
          this.vm.account = res?.account?.Data || [];
          this.createAccountName();
          // mark accounts ready and rebuild index, then rebind any existing detail display
          this.rebuildAccountIndex();
          this.accountsReady = true;
          if (this.pendingRebindAccounts) {
            // run after a microtask to let bindings settle
            Promise.resolve().then(() => this.rebindAccountInputs());
          } else {
            this.rebindAccountInputs();
          }
          this.vm.location = res?.location?.Data || [];
          this.vm.accountSubtotal = res?.accountSubtotal?.Data || [];
          this.vm.company = res?.company?.Data || [];
          this.vm.currency = res?.currency?.Data || [];
          this.vm.rfType = res?.rfType?.Data || [];
          this.vm.reportingFields = res?.reportingFields?.Data || [];

          // Derive rf1..rf5 arrays from single fetch using rfType linkage
          const types: any[] = Array.isArray(this.vm.rfType) ? this.vm.rfType : [];
          const allFields: any[] = Array.isArray(this.vm.reportingFields) ? this.vm.reportingFields : [];

          const findType = (key: string) => {
            const lower = String(key).toLowerCase();
            return (
              types.find((v: any) => String(v?.code).toLowerCase() === lower)
              || types.find((v: any) => String(v?.name).toLowerCase() === lower)
              || types.find((v: any) => String(v?.id).toLowerCase() === lower)
            );
          };

          const rf1Type = findType('rf1');
          const rf2Type = findType('rf2');
          const rf3Type = findType('rf3');
          const rf4Type = findType('rf4');
          const rf5Type = findType('rf5');

          const matchTypeId = (val: any) => (x: any) => String(x?.type_id) === String(val?.id);

          this.vm.rf1 = rf1Type ? allFields.filter(matchTypeId(rf1Type)) : [];
          this.vm.rf2 = rf2Type ? allFields.filter(matchTypeId(rf2Type)) : [];
          this.vm.rf3 = rf3Type ? allFields.filter(matchTypeId(rf3Type)) : [];
          this.vm.rf4 = rf4Type ? allFields.filter(matchTypeId(rf4Type)) : [];
          this.vm.rf5 = rf5Type ? allFields.filter(matchTypeId(rf5Type)) : [];

          // Ensure arrays exist
          this.vm.rf1 = Array.isArray(this.vm.rf1) ? this.vm.rf1 : [];
          this.vm.rf2 = Array.isArray(this.vm.rf2) ? this.vm.rf2 : [];
          this.vm.rf3 = Array.isArray(this.vm.rf3) ? this.vm.rf3 : [];
          this.vm.rf4 = Array.isArray(this.vm.rf4) ? this.vm.rf4 : [];
          this.vm.rf5 = Array.isArray(this.vm.rf5) ? this.vm.rf5 : [];

          // Group by type id map for quick access
          const grouped: Record<string, any[]> = {};
          for (const f of allFields) {
            const key = String(f?.type_id ?? '');
            if (!key) {
              continue;
            }
            if (!grouped[key]) {
              grouped[key] = [];
            }
            grouped[key].push(f);
          }
          this.vm.rfByTypeId = grouped;

          // Build id -> reporting field map for fast lookups in print/email
          const byId: Record<string, any> = {};
          for (const f of allFields) {
            const key = String(f?.id ?? '').trim();
            if (!key) {
              continue;
            }
            byId[key] = f;
          }
          this.vm.rfById = byId;

          this.vm.rfTypeLoaded = true;

          // If an existing entry is being edited and the form has finished its initial
          // normalization (accounts rebound, totals calculated), refresh the baseline so
          // Close does not prompt when no user edits occurred.
          try {
            if (this.vm.isLoaded && this.vm?.item?.header?.id) {
              this.scheduleBaselineRefresh();
            }
          } catch {
          }
        } catch (e) {
          // keep safe defaults
          this.vm.rf1 = this.vm.rf1 || [];
          this.vm.rf2 = this.vm.rf2 || [];
          this.vm.rf3 = this.vm.rf3 || [];
          this.vm.rf4 = this.vm.rf4 || [];
          this.vm.rf5 = this.vm.rf5 || [];
        }
      },
      error: (err: any) => {
        // Non-fatal: load continues with whatever is available
        console.error('Lookup data fetch error', err);
      }
    });
  }

  private getEntryData(): void {
    this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params: ParamMap) => {
      this.vm.entry_id = params.get('entryId');
      this.vm.batchId = params.get('batchId');
      this.vm.recurring = params.get('recurring');
      this.vm.title = this.buildTabTitle(this.normalizeRecurringFlag(this.vm.recurring));
      this.titleService.setTitle(this.vm.title);

      if (this.vm.entry_id === 'new') {
        this.vm.batchTypeId = '1';
      } else {
        this.vm.Empty = 1;
      }
      if (this.vm.Empty == 1) {
        this.vm.item = {
          header: {},
          batch: {type_id: '1', locked: 'false'},
        };
        this.tableDataService
          .getData('entry', this.vm.entry_id)
          .subscribe((result: any[]) => {
            this.vm.item = result;
            this.vm.title = this.buildTabTitle(this.normalizeRecurringFlag(this.vm?.item?.header?.recurring));
            this.titleService.setTitle(this.vm.title);
            this.vm.item.header.header_date = this.dateFilter.GetDateFormat(
              this.vm.item.header.header_date
            );
            if (this.vm.item.header.reversal_date) {
              this.vm.item.header.create_reversal = true;
              this.vm.item.header.reversal_date = this.dateFilter.GetDateFormat(
                this.vm.item.header.reversal_date
              );
            }
            // Populate intercompany fields from header (API stores them on header, not root)
            const h = this.vm.item.header;
            this.vm.item.create_intercompany = h.create_intercompany === true || !!h.intercompany_company_id;
            this.vm.item.intercompany_company_id = h.intercompany_company_id ?? null;
            this.vm.item.intercompany_location_id = h.intercompany_location_id ?? null;
            if (!this.vm.item.header.periodicity) {
              this.vm.item.header.periodicity = 'monthly';
            }
            if (this.vm.item.header.recurring_end_date) {
              this.vm.item.header.recurring_end_date = this.dateFilter.GetDateFormat(
                this.vm.item.header.recurring_end_date
              );
            }
            /*console.log('[EntryForm] IC load:', {
              create_intercompany: this.vm.item.create_intercompany,
              intercompany_company_id: this.vm.item.intercompany_company_id,
              intercompany_location_id: this.vm.item.intercompany_location_id
            });*/
            this.buttonsDisabled = !!this.vm.item.batch.locked;
            this.init();
            this.vm.isLoaded = true;
          });
      } else {
        this.init();
      }
    });
  }

  private normalizeRecurringFlag(value: any): boolean {
    if (typeof value === 'boolean') {
      return value;
    }

    const normalized = String(value ?? '').trim().toLowerCase();
    return normalized === 'true' || normalized === '1' || normalized === 'yes';
  }

  private buildTabTitle(isRecurring: boolean): string {
    const entryLabel = isRecurring
      ? `${$localize`:Recurring @@recurring:Recurring`} ${$localize`:Journal Entry @@journalEntry:Journal Entry`}`
      : $localize`:Journal Entry @@journalEntry:Journal Entry`;

    const prefix = this.vm.entry_id === 'new'
      ? $localize`:New @@new:New`
      : $localize`:Edit @@edit:Edit`;

    return `${prefix} ${entryLabel}`;
  }

  onIntercompanyChange(event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    if (checked && !this.vm.item.header.company_id) {
      this.notification.showWarningToast(
        $localize`:Please select a Company before enabling Intercompany. @@selectCompanyBeforeIntercompany:Please select a Company before enabling Intercompany.`
      );
      this.vm.item.create_intercompany = false;
    }
    if (!checked) {
      this.vm.item.intercompany_company_id = null;
      this.vm.item.intercompany_location_id = null;
    }
  }
}
